import { GridColDef, GridRenderCellParams, GridRowModel } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useDeviceById } from "@/hooks/devices/useDeviceById";
import { CustomProperty } from "@/models/CustomProperty";

interface Props {
  deviceId: string;
}

export default function CustomDeviceProperties({ deviceId }: Props) {
  const { data: device } = useDeviceById(deviceId);
  const { t } = useTranslation();

  const toDisplayValue = (val: unknown, type?: string): string => {
    if (val === undefined || val === null || val === "") return "";

    const raw = String(val).trim().toLowerCase();
    if (type === "Boolean") {
      return raw === "true" ? t("common.yes") : t("common.no");
    }
    return String(val);
  };

  const rows: GridRowModel[] = useMemo(() => {
    const baseRows =
      ((device?.customProperties as CustomProperty[]) ?? []).map(cp => ({
        id: cp.property.id,
        name: cp.property.name,
        _valueRaw: cp.value,
        _type: cp.property.propertyType,
      })) ?? [];

    // Always include VIN rows
    const vinRows = [
      {
        id: "vin-engine",
        name: "VIN (" + t("device") + ")",
        // @ts-ignore
        _valueRaw: device?.engineVehicleIdentificationNumber ?? "",
        _type: "String",
      },
      {
        id: "vin-mygeotab",
        name: "VIN (MyGeotab)",
        // @ts-ignore
        _valueRaw: device?.vehicleIdentificationNumber ?? "",
        _type: "String",
      },
    ];

    return [...vinRows, ...baseRows];
  }, [
    device?.customProperties,
    // @ts-ignore
    device?.engineVehicleIdentificationNumber,
    // @ts-ignore
    device?.vehicleIdentificationNumber,
    t,
  ]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("faultOverview.customPropertyName", "Name"),
      flex: 1,
      type: "string",
    },
    {
      field: "value",
      headerName: t("faultOverview.customPropertyValue", "Value"),
      flex: 1,
      type: "string",
      valueGetter: (_, row) => {
        return toDisplayValue(row._valueRaw, row._type);
      },
      renderCell: (params: GridRenderCellParams) => {
        const text = toDisplayValue(params.row._valueRaw, params.row._type);
        return <span>{text}</span>;
      },
    },
  ];

  return (
    <I18nGrid
      rows={rows}
      columns={columns}
      density="compact"
      hideFooter
      disableAutosize
      disableColumnResize
      disableColumnPinning
      disableColumnReorder
      disableRowSelectionOnClick
    />
  );
}
